// 📄 components/shared/ImportarColeccionModal.tsx
// "Importar colección (OpenAPI / Postman)" — wizard de 3 pasos que parsea
// un archivo JSON 100% client-side (ver utils/apiCollectionParser.ts) y
// agrega los endpoints elegidos al formulario de la plantilla. Mismo
// criterio de credenciales que ImportarCurlModal.tsx (modo "catalogo"):
// nunca se importan headers de autenticación, solo se avisa cuántos se
// omitieron. No reutiliza XmlDropzone.tsx (acoplado a XML/parseXmlString)
// — replica su mismo patrón visual de drag&drop para .json.
import { useMemo, useRef, useState } from "react";
import { X, UploadCloud, Package, AlertTriangle, Info, Search, CheckCircle2 } from "lucide-react";
import { ApiEndpointConfig, ApiEndpointMetodo } from "../../services/clienteConexionService.ts";
import {
  parsearColeccion, endpointColeccionAApiEndpointConfig,
  EndpointColeccion, ResultadoParseoColeccion
} from "../../utils/apiCollectionParser.ts";

interface ImportarColeccionModalProps {
  // Cuántos endpoints ya tiene el formulario antes de importar — solo para
  // el aviso "se agregarán N a los M existentes" (ver Paso 2).
  endpointsExistentes: number;
  onClose: () => void;
  onImportar: (nuevos: ApiEndpointConfig[]) => void;
}

type Paso = "subir" | "seleccionar" | "resultado";

const COLOR_METODO: Record<ApiEndpointMetodo, string> = {
  GET: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  POST: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  PUT: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  PATCH: "text-purple-300 bg-purple-500/10 border-purple-500/30",
  DELETE: "text-red-300 bg-red-500/10 border-red-500/30"
};

const FILTROS_METODO: ("ALL" | ApiEndpointMetodo)[] = ["ALL", "GET", "POST", "PUT", "PATCH", "DELETE"];

// Contenido del tooltip nativo (title) de cada fila — summary completo +
// params/headers/path variables detectados, mismo criterio de "title"
// nativo que ya usa el resto del proyecto (no hay componente Tooltip
// custom, ver RestriccionesTagEditor.tsx / FilaDiscrepancia).
const construirTooltip = (e: EndpointColeccion): string => {
  const lineas = [e.descripcion || "(sin descripción)"];
  if (e.params.length > 0) lineas.push(`Params: ${e.params.map((p) => p.clave).join(", ")}`);
  if (e.headers.length > 0) lineas.push(`Headers: ${e.headers.map((h) => h.clave).join(", ")}`);
  if (e.pathVariables.length > 0) lineas.push(`Path variables: ${e.pathVariables.map((v) => v.clave).join(", ")}`);
  return lineas.join("\n");
};

interface ResumenFinal {
  importados: number;
  omitidosMetodo: number;
  headersOmitidosTotal: number;
}

const ENDPOINTS_VACIO: EndpointColeccion[] = [];

export default function ImportarColeccionModal({ endpointsExistentes, onClose, onImportar }: ImportarColeccionModalProps) {
  const [paso, setPaso] = useState<Paso>("subir");
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [resultado, setResultado] = useState<ResultadoParseoColeccion | null>(null);
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [filtroMetodo, setFiltroMetodo] = useState<"ALL" | ApiEndpointMetodo>("ALL");
  const [busqueda, setBusqueda] = useState("");

  const [resumenFinal, setResumenFinal] = useState<ResumenFinal | null>(null);

  const procesarArchivo = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const texto = reader.result as string;
      const parseo = parsearColeccion(texto);
      if (!parseo.ok) {
        setErrorArchivo(parseo.error || "No se pudo procesar el archivo.");
        return;
      }
      setErrorArchivo(null);
      setResultado(parseo);
      // Todos seleccionados por defecto — el admin deselecciona lo que no
      // quiere en vez de tener que marcar uno por uno.
      setSeleccionados(new Set(parseo.endpoints.map((e) => e.id)));
      setPaso("seleccionar");
    };
    reader.onerror = () => setErrorArchivo("No se pudo leer el archivo.");
    reader.readAsText(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) procesarArchivo(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setArrastrando(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      setErrorArchivo("Solo se aceptan archivos .json.");
      return;
    }
    procesarArchivo(file);
  };

  // Referencia estable cuando no hay resultado todavía — un `[]` literal
  // inline en cada render rompería la memoización de endpointsFiltrados.
  const endpoints = resultado?.endpoints || ENDPOINTS_VACIO;

  const endpointsFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return endpoints.filter((e) => {
      if (filtroMetodo !== "ALL" && e.metodo !== filtroMetodo) return false;
      if (!q) return true;
      return e.path.toLowerCase().includes(q) || e.descripcion.toLowerCase().includes(q);
    });
  }, [endpoints, filtroMetodo, busqueda]);

  const todosFiltradosSeleccionados = endpointsFiltrados.length > 0 && endpointsFiltrados.every((e) => seleccionados.has(e.id));

  const toggleSeleccionado = (id: string) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // "Seleccionar/deseleccionar todos" opera sobre lo que está visible con
  // el filtro activo, no sobre la lista completa — así filtrar por método
  // y seleccionar todo ese subconjunto es un flujo de un solo click.
  const toggleTodosFiltrados = () => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (todosFiltradosSeleccionados) {
        endpointsFiltrados.forEach((e) => next.delete(e.id));
      } else {
        endpointsFiltrados.forEach((e) => next.add(e.id));
      }
      return next;
    });
  };

  const cantidadSeleccionados = seleccionados.size;

  const handleConfirmarImportacion = () => {
    const elegidos = endpoints.filter((e) => seleccionados.has(e.id));
    if (elegidos.length === 0) return;

    const nuevos = elegidos.map(endpointColeccionAApiEndpointConfig);
    const headersOmitidosTotal = elegidos.reduce((acc, e) => acc + e.headersOmitidos.length, 0);

    onImportar(nuevos);
    setResumenFinal({
      importados: nuevos.length,
      omitidosMetodo: resultado?.omitidos || 0,
      headersOmitidosTotal
    });
    setPaso("resultado");
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl my-8 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-400" /> Importar colección (OpenAPI / Postman)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {paso === "subir" && (
            <>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
                  arrastrando ? "border-indigo-400 bg-indigo-500/10" : "border-white/15 hover:border-white/30"
                }`}
              >
                <UploadCloud className={`w-8 h-8 ${arrastrando ? "text-indigo-400" : "text-gray-500"} transition-colors`} />
                <p className="text-sm text-gray-300">
                  Arrastra tu archivo JSON aquí o <span className="text-indigo-400 font-semibold">haz click para seleccionar</span>
                </p>
                <p className="text-xs text-gray-500">Se aceptan archivos OpenAPI/Swagger JSON o colecciones Postman v2.0/v2.1</p>
                <input ref={inputRef} type="file" accept=".json,application/json" onChange={handleInputChange} className="hidden" />
              </div>

              {errorArchivo && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {errorArchivo}
                </div>
              )}
            </>
          )}

          {paso === "seleccionar" && resultado && (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="text-sm font-semibold text-white">{resultado.nombreColeccion}</h4>
                  <p className="text-xs text-gray-500">{endpoints.length} endpoint{endpoints.length !== 1 ? "s" : ""} encontrado{endpoints.length !== 1 ? "s" : ""}</p>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                  {resultado.versionLabel}
                </span>
              </div>

              {resultado.omitidos > 0 && (
                <p className="flex items-start gap-1.5 text-[11px] text-amber-400/80">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                  {resultado.omitidos} endpoint{resultado.omitidos !== 1 ? "s" : ""} con método no soportado — se omitirá{resultado.omitidos !== 1 ? "n" : ""} automáticamente.
                </p>
              )}

              {endpointsExistentes > 0 && (
                <p className="flex items-start gap-1.5 text-[11px] text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-2.5">
                  <Info size={12} className="flex-shrink-0 mt-0.5" />
                  Se agregarán {cantidadSeleccionados} endpoint{cantidadSeleccionados !== 1 ? "s" : ""} a los {endpointsExistentes} existentes.
                </p>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={todosFiltradosSeleccionados}
                    onChange={toggleTodosFiltrados}
                    className="accent-indigo-500 w-3.5 h-3.5"
                  />
                  {todosFiltradosSeleccionados ? "Deseleccionar todos" : "Seleccionar todos"}
                </label>

                <div className="flex items-center gap-1 flex-wrap">
                  {FILTROS_METODO.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFiltroMetodo(m)}
                      className={`text-[11px] font-bold px-2 py-1 rounded-lg border transition-colors ${
                        filtroMetodo === m ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white/5 border-white/10 text-gray-400 hover:text-white"
                      }`}
                    >
                      {m === "ALL" ? "ALL" : m}
                    </button>
                  ))}
                </div>

                <div className="relative flex-1 min-w-[160px]">
                  <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por path o descripción..."
                    className={`${inputClass} pl-8 py-1.5 text-xs`}
                  />
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
                {endpointsFiltrados.length === 0 ? (
                  <p className="text-xs text-gray-500 py-4 text-center">Ningún endpoint coincide con el filtro.</p>
                ) : (
                  endpointsFiltrados.map((e) => (
                    <label
                      key={e.id}
                      title={construirTooltip(e)}
                      className="flex items-center gap-3 p-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={seleccionados.has(e.id)}
                        onChange={() => toggleSeleccionado(e.id)}
                        className="accent-indigo-500 w-3.5 h-3.5 flex-shrink-0"
                      />
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border flex-shrink-0 ${COLOR_METODO[e.metodo]}`}>
                        {e.metodo}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-mono text-gray-200 truncate">{e.path}</span>
                        {e.descripcion && <span className="block text-[11px] text-gray-500 truncate">{e.descripcion}</span>}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}

          {paso === "resultado" && resumenFinal && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-bold">
                <CheckCircle2 className="w-5 h-5" />
                {resumenFinal.importados} endpoint{resumenFinal.importados !== 1 ? "s" : ""} importado{resumenFinal.importados !== 1 ? "s" : ""} correctamente
              </div>

              {resumenFinal.omitidosMetodo > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-400/80">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  {resumenFinal.omitidosMetodo} endpoint{resumenFinal.omitidosMetodo !== 1 ? "s" : ""} omitido{resumenFinal.omitidosMetodo !== 1 ? "s" : ""} por tener un método no soportado.
                </p>
              )}

              {resumenFinal.headersOmitidosTotal > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-400/80">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  Se omitieron {resumenFinal.headersOmitidosTotal} header{resumenFinal.headersOmitidosTotal !== 1 ? "s" : ""} de autenticación (API Key, Authorization) — configúralos por cliente en Configuración.
                </p>
              )}

              <p className="flex items-start gap-1.5 text-xs text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                Recuerda verificar la URL Base y API Path de la plantilla — todos los endpoints importados la comparten.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-white/10 flex-shrink-0">
          {paso === "seleccionar" ? (
            <span className="text-xs text-gray-500">{cantidadSeleccionados} seleccionado{cantidadSeleccionados !== 1 ? "s" : ""} de {endpoints.length}</span>
          ) : <span />}

          <div className="flex items-center gap-3">
            {paso !== "resultado" && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
            )}

            {paso === "seleccionar" && (
              <button
                type="button"
                onClick={handleConfirmarImportacion}
                disabled={cantidadSeleccionados === 0}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Importar {cantidadSeleccionados} endpoint{cantidadSeleccionados !== 1 ? "s" : ""}
              </button>
            )}

            {paso === "resultado" && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
              >
                Listo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
